import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { TopAppropriatedVehicleDashboardDto } from 'api/dashboard/dtos/TopAppropriatedVehicleDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useInfiniteReveal from '../components/useInfiniteReveal';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import KpiHeader from '../components/KpiHeader';
import styles from './TopAppropriatedVehicles.module.scss';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import WidgetNoData from '../components/WidgetNoData';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 10;

const SCALE_COLORS = [
	'#025a7a',
	'#038cb6',
	'#02c7c7',
	'#6de0e0',
	'#b8f0f0',
];

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const TopAppropriatedVehicles = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<TopAppropriatedVehicleDashboardDto[] | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('topAppropriatedVehicles');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getTopAppropriatedVehicles(criteria);
				if (!cancelled) setData(result);
			} catch {
				if (!cancelled) addToast(t('common.messages.error_load_info'), { appearance: 'error' });
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const items = data || [];
	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(items, PAGE_SIZE);

	const buildVehicleTooltip = (item: TopAppropriatedVehicleDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.top_appropriated_vehicles.title' as any) as string}
			subTitleTooltip={t('dashboard.top_appropriated_vehicles.tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}><LoadingSpinner /></div>
			) : !data || data.length === 0 ? (
				<WidgetNoData />
			) : (
				<>
					<KpiHeader
						value={String(items.length)}
						subLabel={t('dashboard.total_assets' as any) as string}
						currentTotal={items.length}
						previousTotal={null}
						previousRange={null}
					/>
					<div className={styles.list}>
					{visibleItems.map((item, index) => {
						const color = SCALE_COLORS[Math.min(index, SCALE_COLORS.length - 1)];
						return (
							<div key={item.vehicleId} className={styles.row}>
								<div className={styles.nameBlock}>
									<span className={styles.name}>
										<span data-tip={buildVehicleTooltip(item)} data-for="top-appropriated-vehicles-vehicle-tooltip" data-place="right">{item.registrationNumber}</span>
									</span>
									<span className={styles.sub}>
										{item.totalReservations} {t('dashboard.top_appropriated_vehicles.reservations' as any)} &middot; {item.distinctDrivers} {t('dashboard.top_appropriated_vehicles.drivers' as any)}
									</span>
								</div>
								<div className={styles.barTrack}>
									<div className={styles.barFill} style={{ width: `${Math.min(Number(item.dominancePercent), 100)}%`, background: color }} />
								</div>
								<div className={styles.driverBlock}>
									<span className={`${styles.driverLine} ${index === 0 ? styles.driverFirst : ''}`}>
										<span className={styles.driverName}>{item.dominantDriverName}</span>
										<span className={styles.pct}>{Number(item.dominancePercent).toFixed(0)}%</span>
									</span>
									<span className={styles.hours}>
										<span
											className={styles.hoursValue}
											data-tip={t('dashboard.top_appropriated_vehicles.driver_hours_tooltip' as any) as string}
											data-for="top-appropriated-vehicles-hours-tooltip"
										>
											{Number(item.dominantHours).toFixed(1)}h
										</span>
										{' / '}
										<span
											className={styles.hoursValue}
											data-tip={t('dashboard.top_appropriated_vehicles.total_hours_tooltip' as any) as string}
											data-for="top-appropriated-vehicles-hours-tooltip"
										>
											{Number(item.totalHours).toFixed(1)}h
										</span>
									</span>
								</div>
							</div>
						);
					})}
					{hasMore && <div ref={sentinelRef} className={infiniteStyles.sentinel} />}
					</div>
					<ReactTooltip id="top-appropriated-vehicles-hours-tooltip" effect="solid" place="left" />
					<ReactTooltip id="top-appropriated-vehicles-vehicle-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default TopAppropriatedVehicles;
